// Generic, config-driven "questionnaire → jacket flag" engine.
//
// This mirrors PCI's classic-ASP questionnaire engine (QuestionnaireRenderer.asp:
// HandleAllQuestionSideEffects / HandleQuestion / prv_AddOrRemoveQuestionnaireFlag,
// plus FlagInterface.vbs: AddOrDeleteFlagAsNecessary / GetFlagSubTypeFieldName) so
// that ANY questionnaire (Classification, Medical, Gang, Mental Health, ...) and
// ANY question within it gets flag handling for free, purely from config data -
// no per-question or per-questionnaire code required.
//
// Two independent sources can create/remove a cdINVOLVEMENTS ("Jacket Flag") row:
//
//   1. Per-question side effects - a question's own `questionSideEffects` array,
//      where `questionSideEffectTypeID` is Set Jacket Flag (50200003) or Clear
//      Jacket Flag (50200004) and the current answer matches `answerToTriggerOnID`.
//      Example: Classification Q188 "Known gang affiliation?" -> Yes sets the
//      Gang flag (flagTypeID 50030005), independent of any score.
//
//   2. Score-threshold triggers - a question GROUP or the QUESTIONNAIRE itself
//      has `jacketFlagTrigger: true`; once its points reach `scoreThreshold` the
//      flag configured on that group/questionnaire (its own flagTypeID/etc.) is
//      set, and cleared again if the score later drops back below threshold.
//
// Both produce the same shape of "flag descriptor" and are applied to the
// booking's cdInvolvements collection the same way PCI does: add the row only if
// an equivalent one isn't already present, remove it only if one is - so saving
// the same questionnaire repeatedly is idempotent (no duplicate flags) and
// un-checking/re-answering a question retracts a flag it had set.

import { REF_LIST_CODE } from "@/util/refList";
import {
  hasValue,
  getSideEffects,
  findAnswer,
  sideEffectFires,
} from "@/util/questionnaireEngine";

const { SET_JACKET_FLAG, CLEAR_JACKET_FLAG } =
  REF_LIST_CODE.QUESTION_SIDE_EFFECT_TYPE;

// Maps a config-side sub-type field (…TypeID) to the matching cdINVOLVEMENTS
// column (…TypeId) - see FolderFlags.jsx's reduxFieldMapping / createCDIObj in
// MedicalQuestionnaireList.jsx for the same casing convention used elsewhere.
const SUB_TYPE_FIELD_BY_CONFIG_KEY = [
  ["noteTypeID", "noteTypeId"],
  ["medicalTypeID", "medicalTypeId"],
  ["dangerTypeID", "dangerTypeId"],
  ["gangTypeID", "gangTypeId"],
];

// Mirrors PCI's prv_AddOrRemoveQuestionnaireFlag priority order: Notes, then
// Medical, then Danger, then Gang - the first of the four sub-type fields that
// is actually populated on the config row wins. None of them are required;
// when none is set, the flag is still created/cleared using flagTypeID alone
// (this is exactly what happens for "Known gang affiliation?" - only
// flagTypeID is configured, no gangTypeID).
const pickSubType = (source) => {
  for (const [configKey, reduxField] of SUB_TYPE_FIELD_BY_CONFIG_KEY) {
    if (hasValue(source?.[configKey])) {
      return { field: reduxField, value: source[configKey] };
    }
  }
  return { field: null, value: null };
};

// One flag descriptor produced by either source above.
const makeFlagDescriptor = ({ source, action, questionID = null }) => {
  const flagTypeID = source?.flagTypeID ?? source?.FlagType_ID;
  if (!hasValue(flagTypeID)) return null;

  const { field, value } = pickSubType(source);
  return {
    action, // "set" | "clear"
    flagTypeID,
    subTypeField: field,
    subTypeValue: value,
    flagText: source?.flagText ?? source?.FlagText ?? "",
    questionID,
  };
};

/**
 * Walks every question in every group and evaluates its questionSideEffects
 * against the current answers. Returns one descriptor per firing Set/Clear
 * Jacket Flag side effect. Add/Subtract Points, Queue Step and Hilite side
 * effects are ignored here (points are already handled by the questionnaire
 * modal itself; queue step / hilite have no jacket-flag effect).
 */
export const collectQuestionSideEffectFlags = ({
  questionnaire,
  questionAnswers,
}) => {
  const descriptors = [];

  questionnaire?.questionGroups?.forEach((group) => {
    group?.questions?.forEach((question) => {
      const sideEffects = getSideEffects(question);
      if (!sideEffects?.length) return;

      const answer = findAnswer(questionAnswers, question.questionID);

      sideEffects.forEach((sideEffect) => {
        const typeID =
          sideEffect?.questionSideEffectTypeID ??
          sideEffect?.QuestionSideEffectType_ID;
        if (typeID !== SET_JACKET_FLAG && typeID !== CLEAR_JACKET_FLAG) return;
        if (!sideEffectFires(sideEffect, answer)) return;

        const descriptor = makeFlagDescriptor({
          source: sideEffect,
          action: typeID === SET_JACKET_FLAG ? "set" : "clear",
          questionID: question.questionID,
        });
        if (descriptor) descriptors.push(descriptor);
      });
    });
  });

  return descriptors;
};

/**
 * Evaluates the questionnaire-level and question-group-level jacketFlagTrigger
 * / scoreThreshold pair (Sugar 4615 in PCI). Fires "set" once points reach the
 * threshold and "clear" otherwise, so re-saving after an answer change retracts
 * the flag exactly like PCI's HandleAllQuestionSideEffects does every time it
 * runs (it always calls prv_AddOrRemoveQuestionnaireFlag with
 * fTurnFlagOn = CBool(points >= threshold), never only on the way up).
 */
export const collectScoreThresholdFlags = ({
  questionnaire,
  totalPoints,
  questionGroupPoints,
}) => {
  const descriptors = [];

  const evaluate = (source, points) => {
    if (!source?.jacketFlagTrigger) return;
    if (!hasValue(source?.scoreThreshold) || isNaN(source.scoreThreshold)) {
      return;
    }
    const fires = Number(points || 0) >= Number(source.scoreThreshold);
    const descriptor = makeFlagDescriptor({
      source,
      action: fires ? "set" : "clear",
    });
    if (descriptor) descriptors.push(descriptor);
  };

  evaluate(questionnaire, totalPoints);

  questionnaire?.questionGroups?.forEach((group) => {
    const groupPoints = questionGroupPoints?.find(
      (qgp) => qgp.questionGroupId === group.questionGroupID,
    )?.totalPoints;
    evaluate(group, groupPoints);
  });

  return descriptors;
};

// Builds the cdINVOLVEMENTS-shaped row for a "set" descriptor. Matches the
// field casing already established by FolderFlags.jsx (manual Add Flag) and
// MedicalQuestionnaireList.jsx's createCDIObj (score-threshold flags).
const buildInvolvementRow = (descriptor, context) => {
  const row = {
    invCreatedBy: context.invCreatedBy ?? null,
    invObjType: 3,
    invObjId: context.invObjId ?? null,
    invCreatedDate: context.invCreatedDate,
    linkedObjectType: "Folders",
    relatedObjectType: context.relatedObjectType,
    relatedObjectID: context.relatedObjectID ?? null,
    flagTypeID: descriptor.flagTypeID,
    invExpireDate: null,
    invDesc: descriptor.flagText || null,
  };
  if (descriptor.subTypeField) {
    row[descriptor.subTypeField] = descriptor.subTypeValue;
  }
  return row;
};

// Whether an existing cdINVOLVEMENTS row is "the same flag" as a descriptor -
// used both to skip re-adding an already-present flag and to find which
// row(s) a "clear" should remove. Mirrors PCI's IsFlagPresentWithCollection
// (FlagInterface.vbs): matched on flagTypeID + the booking it's attached to
// (invObjId) + sub-type + text, AND - only once we actually have one -
// relatedObjectType/relatedObjectID (e.g. this exact
// InmateClassificationQuestionnaireID). ASP's own Trim(RelatedObjectID) <> ""
// check only adds RelatedObjectID/RelatedObjectType to its match criteria
// when that ID is non-blank; we do the same via hasValue(relatedObjectID)
// below, since a brand-new, not-yet-saved questionnaire has no ID yet
// (relatedObjectID stays null until Save - see createCDIObj/getAnswer in the
// questionnaire list components).
//
// This means: a flag added manually via Folder Flags, or set by a *different*
// questionnaire instance (its own relatedObjectID), is NOT touched by this
// questionnaire's Set/Clear side effects - only a flag this same record
// previously set (same relatedObjectType + relatedObjectID) is found again on
// re-edit and cleared/updated. That's what makes re-saving idempotent without
// also making Clear reach into flags this questionnaire never created.
const isSameFlag = (row, descriptor, context) => {
  if (!row || row.flagTypeID !== descriptor.flagTypeID) return false;
  if (
    hasValue(context.invObjId) &&
    row.invObjId !== context.invObjId &&
    !(hasValue(context.altInvObjId) && row.invObjId === context.altInvObjId)
  ) {
    return false;
  }
  if (hasValue(context.relatedObjectID)) {
    if (row.relatedObjectID !== context.relatedObjectID) return false;
    if ((row.relatedObjectType || "") !== (context.relatedObjectType || "")) {
      return false;
    }
  }
  if (descriptor.flagText && (row.invDesc || "") !== descriptor.flagText) {
    return false;
  }
  if (descriptor.subTypeField) {
    if (row[descriptor.subTypeField] !== descriptor.subTypeValue) return false;
  }
  return true;
};

/**
 * Applies a list of flag descriptors (from collectQuestionSideEffectFlags
 * and/or collectScoreThresholdFlags) to the booking's current cdInvolvements
 * array. Returns the updated array plus the invId of any row that was removed
 * and already existed in the DB (so the caller can queue a DELETE), mirroring
 * PCI's AddOrDeleteFlagAsNecessary (add only if not already present, delete
 * only if present).
 */
export const applyFlagDescriptors = (
  cdInvolvements,
  descriptors,
  context,
) => {
  let rows = [...(cdInvolvements || [])];
  const removedInvIds = [];

  descriptors.forEach((descriptor) => {
    if (descriptor.action === "set") {
      const alreadyPresent = rows.some((row) =>
        isSameFlag(row, descriptor, context),
      );
      if (!alreadyPresent) {
        rows.push(buildInvolvementRow(descriptor, context));
      }
    } else {
      rows = rows.filter((row) => {
        const match = isSameFlag(row, descriptor, context);
        if (match && hasValue(row.invId)) removedInvIds.push(row.invId);
        return !match;
      });
    }
  });

  return { cdInvolvements: rows, removedInvIds };
};

/**
 * Convenience one-call wrapper: evaluates both flag sources for a submitted
 * questionnaire answer set and applies them to cdInvolvements.
 *
 * @param {object} params
 * @param {object} params.questionnaire - the full questionnaire config
 *   (questionGroups[].questions[].questionSideEffects, plus jacketFlagTrigger/
 *   scoreThreshold/flagTypeID/etc. on the questionnaire and each group).
 * @param {Array}  params.questionAnswers - [{ questionId, answer }]
 * @param {Array}  params.questionGroupPoints - [{ questionGroupId, totalPoints }]
 * @param {number} params.totalPoints
 * @param {Array}  params.cdInvolvements - the booking's current flags
 * @param {object} params.context - { invObjId, invCreatedBy, invCreatedDate,
 *   relatedObjectType, relatedObjectID }
 */
export const evaluateAndApplyQuestionnaireFlags = ({
  questionnaire,
  questionAnswers,
  questionGroupPoints,
  totalPoints,
  cdInvolvements,
  context,
}) => {
  if (!questionnaire) return { cdInvolvements: cdInvolvements || [], removedInvIds: [] };

  const descriptors = [
    ...collectQuestionSideEffectFlags({ questionnaire, questionAnswers }),
    ...collectScoreThresholdFlags({
      questionnaire,
      totalPoints,
      questionGroupPoints,
    }),
  ];

  return applyFlagDescriptors(cdInvolvements, descriptors, context);
};
